import { addMonths, format, isSameMonth, parseISO, startOfMonth } from 'date-fns'
import { tr } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCalendarEvents } from '../api'
import type { CalendarEventKind, WidgetBodyProps } from '../types'
import { WidgetState } from './WidgetItems'

const KIND_TILE: Record<CalendarEventKind, string> = {
  meeting: 'bg-primary/10 text-primary',
  call: 'bg-warning/15 text-warning-strong',
  demo: 'bg-success/12 text-success',
  review: 'bg-violet/12 text-violet',
}

export function CalendarWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useCalendarEvents()
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const compact = config.density === 'compact'
  const events = (q.data ?? [])
    .filter((e) => isSameMonth(parseISO(e.starts_at), month))
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex items-center gap-1">
        <p className="mr-auto text-[13px] font-semibold">{format(month, 'LLLL yyyy', { locale: tr })}</p>
        <Button variant="ghost" size="icon" className="size-7" aria-label={t('common.prevMonth')} onClick={() => setMonth((m) => addMonths(m, -1))}>
          <ChevronLeft />
        </Button>
        <Button variant="ghost" size="icon" className="size-7" aria-label={t('common.nextMonth')} onClick={() => setMonth((m) => addMonths(m, 1))}>
          <ChevronRight />
        </Button>
        <Button variant="outline" size="sm" className="h-7 px-2.5" onClick={() => setMonth(startOfMonth(new Date()))}>
          {t('common.today')}
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <WidgetState query={q} empty={events.length === 0} emptyText={t('dashboard.widgets.calendar.empty')}>
          <ul className={compact ? 'space-y-1' : 'space-y-1.5'}>
            {events.map((e) => {
              const date = parseISO(e.starts_at)
              return (
                <li key={e.id} className={cn('flex items-center gap-3 rounded-xl bg-muted/40 transition-colors hover:bg-muted/70 dark:bg-muted/20', compact ? 'p-1.5' : 'p-2')}>
                  <div className={cn('flex shrink-0 flex-col items-center justify-center rounded-lg leading-tight', compact ? 'size-9' : 'size-11', KIND_TILE[e.kind])}>
                    <span className="text-sm font-bold tabular-nums">{format(date, 'd')}</span>
                    <span className="text-[10px] font-medium opacity-80">{format(date, 'EEE', { locale: tr })}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium">{e.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{e.subtitle}</p>
                  </div>
                  <div className="shrink-0 text-right leading-tight">
                    <p className="text-xs font-semibold tabular-nums">{format(date, 'HH:mm')}</p>
                    <p className="hidden text-[11px] text-muted-foreground @xs:block">{t(e.online ? 'dashboard.widgets.calendar.online' : 'dashboard.widgets.calendar.onsite')}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        </WidgetState>
      </div>
    </div>
  )
}
